import type { ILegalText } from "~/models/ILegalText";
import type {
  IBookmark,
  ILegalTextParagraph,
} from "~/models/ILegalTextParagraph";
import type { ColorPicker } from "~/types/ColorPicker";
import { stripParagraphPropertiesForLocalStorage } from "../utils/stripParagraphPropertiesForLocalStorage";

const createLocalBookmark = (
  colors: ColorPicker,
  paragraph: ILegalTextParagraph,
  legalText: ILegalText
) => {
  const localBookmark: IBookmark = {
    ...colors,
    legal_text_paragraph_id: paragraph.id,
    legal_text_paragraph: stripParagraphPropertiesForLocalStorage(
      paragraph,
      legalText
    ) as ILegalTextParagraph & { legal_text: ILegalText },
    id: Date.now(),
    type: "bookmark",
    editable: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    deleted_at: undefined,
  };

  return localBookmark;
};

export function useBookmarks(legalText?: MaybeRef<ILegalText | undefined>) {
  const { loggedIn } = storeToRefs(useUserStore());
  const localBookmarks = useLocalBookmarks();

  const { data: serverBookmarks } = useQuery({
    ...queries().bookmarks.all,
    enabled: loggedIn,
  });

  const bookmarks = computed(() =>
    loggedIn.value ? serverBookmarks.value : localBookmarks.value
  );

  const {
    createBookmarkMutation,
    updateBookmarkMutation,
    deleteBookmarkMutation,
  } = bookmarksMutations();

  const addBookmark = async (
    color: ColorPicker,
    paragraph?: ILegalTextParagraph
  ) => {
    const legalTextValue = toValue(legalText);
    if (!paragraph || !legalTextValue) {
      return;
    }

    if (loggedIn.value) {
      const { mutateAsync } = createBookmarkMutation;
      await mutateAsync({ color, paragraphId: paragraph.id });
    } else {
      const localBookmark = createLocalBookmark(
        color,
        paragraph,
        legalTextValue
      );
      localBookmarks.value = [...localBookmarks.value, localBookmark];
    }
  };

  const updateBookmark = async (id: number, color: ColorPicker) => {
    if (loggedIn.value) {
      const { mutateAsync } = updateBookmarkMutation;
      await mutateAsync({ id, color });
    } else {
      const index = localBookmarks.value.findIndex((b) => b.id === id);
      if (index !== -1) {
        const updatedBookmark: IBookmark = {
          ...localBookmarks.value[index],
          ...color,
        };
        localBookmarks.value = [
          ...localBookmarks.value.slice(0, index),
          updatedBookmark,
          ...localBookmarks.value.slice(index + 1),
        ];
      }
    }
  };

  const deleteBookmark = async (id: number) => {
    if (loggedIn.value) {
      await deleteBookmarkMutation.mutateAsync(id);
    } else {
      localBookmarks.value = localBookmarks.value.filter((b) => b.id !== id);
    }
  };

  return {
    bookmarks,
    addBookmark,
    deleteBookmark,
    updateBookmark,
  };
}
